import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useFormikContext} from 'formik';
import type {Field as FieldType} from 'components/customer/Product/entity/Field';
import type {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {
    DrawerFields,
    HardwareType,
} from 'components/customer/Product/entity/Drawer';
import {
    MIXED,
    faceSpace,
    minimumFaceHeight,
    sharedValue,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {useDrawerParams} from 'components/customer/Product/Drawer/helpers/useDrawerParams';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {
    useGetDrawerSystemQuery,
    useGetRunnerQuery,
} from 'components/customer/Product/store/drawerRunnerApi';
import {useRunnerRequestParams} from 'components/customer/Product/Drawer/helpers/useRunnerRequestParams';
import type {DrawerRunner as DrawerRunnerType} from 'components/customer/Product/entity/DrawerRunner';
import {useAvailableDrawerSystems} from 'components/customer/Product/Drawer/helpers/useAvailableDrawerSystems';
import {
    HardwareDefaultsRow,
    Option,
} from 'components/customer/Product/Specs/HardwareDefaultsRow';

interface DrawerHardwareDefaultsProps {
    field: FieldType;
    fieldset: Fieldset;
}

// Cabinet-wide type and runner; picking one replaces the value on every drawer.
export const DrawerHardwareDefaults = ({
    field,
    fieldset,
}: DrawerHardwareDefaultsProps) => {
    const drawerParams = useDrawerParams(field, fieldset);
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const {defaults, remember, chooseType, offerShortestRunner} =
        useDrawerDefaults();
    const {data: drawerSystems} = useGetDrawerSystemQuery();
    const availableSystems = useAvailableDrawerSystems();

    const typeField = drawerParams.drawerTypeFieldName;
    const runnerField = drawerParams.drawerRunnerFieldName;
    const faceHeightField = drawerParams.drawerFaceHeightFieldName;
    const styleField = drawerParams.drawerStyleFieldName;
    const drawers = values.drawers ?? [];

    const current = useMemo(
        () => ({
            type: sharedValue(drawers, typeField),
            runner: sharedValue(drawers, runnerField),
        }),
        [drawers, typeField, runnerField]
    );

    // hold what they agree on, so a later disagreement still has a default
    useEffect(() => {
        const agreed: Record<string, string> = {};

        if (current.type !== '' && current.type !== MIXED) {
            agreed.type = current.type;
        }

        if (current.runner !== '' && current.runner !== MIXED) {
            agreed.runner = current.runner;
        }

        remember(agreed);
    }, [current, remember]);

    // Nothing records the cabinet's runner, so a saved product whose faces
    // differ reopens with no default to judge them against. The runner an
    // equal layout would be given stands in for it.
    const equalHeight = useMemo(() => {
        const total = toNumber(values.cabinet_total_drawer_height);
        const margin = (name: string) => toNumber(values[String(name)]);

        if (!(total > 0) || drawers.length === 0) return 0;

        const space = faceSpace(
            total,
            margin(drawerParams.drawerTopMarginFieldName),
            margin(drawerParams.drawerBottomMarginFieldName),
            margin(drawerParams.drawerGapFieldName),
            drawers.length
        );

        return space > 0 ? Math.round((space / drawers.length) * 10) / 10 : 0;
    }, [values, drawers.length, drawerParams]);

    const {params} = useRunnerRequestParams(
        0,
        false,
        false,
        drawerParams.drawerTypeFieldName,
        drawerParams.maxRunnerDepth,
        drawerParams.drawerFaceHeightFieldName,
        drawerParams.fingerPullFieldName
    );

    // asked of the equal share, not of one drawer, so the list is what the
    // cabinet as a whole can take
    const equalParams = useMemo(
        () => ({...params, faceHeight: equalHeight}),
        [params, equalHeight]
    );

    // currentData, not data: while a new type fetches, data still holds the old
    // type's runners and the list below would latch those, a change behind
    const {currentData: available} = useGetRunnerQuery(equalParams, {
        skip: equalParams.insertType == null || !(equalHeight > 0),
    });

    // only when they arrive already disagreeing; the effect above holds
    // whatever they agree on
    useEffect(() => {
        if (defaults.runner !== '' || current.runner !== MIXED) return;

        const first = available?.[0];

        if (first) remember({runner: first.name});
    }, [defaults.runner, current.runner, available, remember]);

    // Held once, so a shrinking face cannot take options away mid edit. Only a
    // different type or depth refetches it, having genuinely different runners.
    const offered = useRef<{
        insertType: number;
        depth: number;
        runners: DrawerRunnerType[];
    }>(null);

    if (
        available?.length &&
        (offered.current?.insertType !== params.insertType ||
            offered.current?.depth !== params.depth)
    ) {
        offered.current = {
            insertType: params.insertType,
            depth: params.depth,
            runners: available,
        };
    }

    // records not labels: the live query narrows, so look up what was offered
    const held = offered.current?.runners ?? [];
    const heldType = offered.current?.insertType;

    // the floor every face is held to; the list only filters on a lower bound,
    // so it always carries the type's shortest runner
    const shortest = held.reduce(
        (lowest, runner) =>
            runner.runnerHeight > 0 &&
            (lowest === 0 || runner.runnerHeight < lowest)
                ? runner.runnerHeight
                : lowest,
        0
    );

    useEffect(() => {
        if (shortest > 0) {
            offerShortestRunner({type: heldType, height: shortest});
        }
    }, [shortest, heldType, offerShortestRunner]);

    const runnerOptions = useMemo<Option[]>(
        () => held.map((runner) => ({label: runner.name, value: runner.name})),
        [held]
    );

    const typeOptions = useMemo<Option[]>(
        () =>
            availableSystems.map((system) => ({
                label: system.name,
                value: String(system.id),
                disabled: system.disabled,
            })),
        [availableSystems]
    );

    const applyToAll = useCallback(
        (fieldName: string, value: string | number) => {
            void setTouched({drawers: true});

            drawers.forEach((drawer, index) => {
                void setFieldValue(`drawers[${index}].${fieldName}`, value);
            });
        },
        [drawers, setFieldValue, setTouched]
    );

    const typeHandler = useCallback(
        (option: Option) => {
            if (option && option.value !== MIXED) {
                chooseType(option.value);
                applyToAll(typeField, Number(option.value));
            }
        },
        [applyToAll, chooseType, typeField]
    );

    const runnerHandler = useCallback(
        (option: Option) => {
            if (!option || option.value === MIXED) return;

            const runner = held.find((item) => item.name === option.value);

            if (!runner) return;

            remember({runner: runner.name});

            const autoCount = drawers.filter(
                (drawer) => toNumber(drawer?.[String(styleField)]) === 1
            ).length;
            let autosLeft = autoCount;

            void setTouched({drawers: true});

            drawers.forEach((drawer, index) => {
                const at = (name: string) => `drawers[${index}].${name}`;

                void setFieldValue(at(runnerField), runner.name);
                void setFieldValue(
                    at('drawer_runner_height'),
                    runner.runnerHeight
                );
                void setFieldValue(at('hardware'), [
                    {
                        hardware_id: runner.hardware_id,
                        hardware: {type: HardwareType.RUNNER},
                    },
                ]);

                // grow a face below the new minimum; the autos give up the
                // difference, keeping the shape
                const minimum = minimumFaceHeight(
                    drawer,
                    drawerSystems,
                    typeField,
                    runner.runnerHeight
                );
                const height = toNumber(drawer?.[String(faceHeightField)]);
                const isAuto = toNumber(drawer?.[String(styleField)]) === 1;

                // one drawer has to stay auto to absorb the remainder
                if (height >= minimum || (isAuto && autosLeft <= 1)) return;

                if (isAuto) autosLeft -= 1;

                void setFieldValue(at(faceHeightField), minimum);
                void setFieldValue(at(styleField), 0);
            });
        },
        [
            held,
            drawers,
            drawerSystems,
            faceHeightField,
            runnerField,
            remember,
            setFieldValue,
            setTouched,
            styleField,
            typeField,
        ]
    );

    if (drawers.length === 0) return null;

    return (
        <HardwareDefaultsRow
            typeOptions={typeOptions}
            type={current.type}
            onTypeChange={typeHandler}
            runnerOptions={runnerOptions}
            runner={current.runner}
            onRunnerChange={runnerHandler}
        />
    );
};
